import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { ArrowRight, Play } from "lucide-react";
import type { VideoLesson } from "@/content/videoLessons";
import {
  campaignParamsFromQuery,
  withCampaignParams,
} from "@/lib/marketing/campaignAttribution";

type Props = {
  lesson: VideoLesson;
  priority?: boolean;
};

export default function VideoLessonCard({ lesson, priority = false }: Props) {
  const router = useRouter();
  const lessonHref = withCampaignParams(
    `/videos/${lesson.slug}`,
    campaignParamsFromQuery(router.query),
  );

  return (
    <article className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-900">
      <Link href={lessonHref} className="block" prefetch={false}>
        <div className="relative aspect-[4/5] overflow-hidden bg-slate-950">
          <Image
            src={lesson.posterPath}
            alt={`Cover for ${lesson.title}`}
            fill
            priority={priority}
            sizes="(max-width: 768px) 88vw, (max-width: 1200px) 44vw, 360px"
            className="object-cover transition duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
          <span className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/95 px-3 py-1.5 text-xs font-black text-slate-950 shadow">
            <Play className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
            {lesson.durationLabel}
          </span>
        </div>
        <div className="p-6">
          <p className="mb-2 text-[11px] font-black uppercase tracking-[0.18em] text-amber-700 dark:text-amber-300">
            Free UK law study lesson
          </p>
          <h3 className="text-xl font-black leading-tight text-slate-950 dark:text-white">
            {lesson.shortTitle}
          </h3>
          <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
            {lesson.description}
          </p>
          <span className="mt-5 inline-flex items-center gap-2 text-sm font-black text-emerald-800 dark:text-emerald-300">
            Watch and read the lesson
            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
          </span>
        </div>
      </Link>
    </article>
  );
}
